import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Settings, Save, RotateCcw, TrendingUp, Percent, DollarSign, Building, Target, Gauge, BarChart3, CalendarDays, Users, Plus, CloudRain, Wallet } from "lucide-react";
import { toast } from "sonner";
import TARGETS_CONFIG from "@/lib/targets";
import { formatCurrency, MONTHS } from "@/lib/config";

const MOIS_COURT = ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"];
const DEPTS = [
  { key: "PRQC", label: "Peinture (PRQC)", color: "emerald" },
  { key: "LMDS", label: "LMDS", color: "blue" },
  { key: "LOCATION", label: "Location équipements", color: "amber" },
];

// Deep merge côté frontend : garantit que toutes les catégories TARGETS_CONFIG sont toujours envoyées
function deep_merge_js(base, override) {
  const result = { ...base };
  for (const key of Object.keys(override || {})) {
    if (key in result && typeof result[key] === 'object' && !Array.isArray(result[key]) &&
        typeof override[key] === 'object' && !Array.isArray(override[key])) {
      result[key] = deep_merge_js(result[key], override[key]);
    } else if (override[key] !== null && override[key] !== undefined) {
      result[key] = override[key];
    }
  }
  return result;
}

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const CATEGORY_ICONS = {
  rentabilite: TrendingUp,
  revenus: DollarSign,
  production: Gauge,
  structure: Building,
  marketing: BarChart3
};

const CATEGORY_LABELS = {
  rentabilite: "Rentabilité",
  revenus: "Revenus",
  production: "Production & Efficacité",
  structure: "Structure & Stabilité",
  marketing: "Marketing & Commercial"
};

const CATEGORY_COLORS = {
  rentabilite: "bg-emerald-100 text-emerald-700",
  revenus: "bg-blue-100 text-blue-700",
  production: "bg-amber-100 text-amber-700",
  structure: "bg-purple-100 text-purple-700",
  marketing: "bg-rose-100 text-rose-700"
};

export const ConfigurationCibles = () => {
  const [targets, setTargets] = useState(TARGETS_CONFIG);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  // Objectifs CA mensuels
  const currentYear = new Date().getFullYear();
  const [objYear, setObjYear] = useState(currentYear);
  const [objCA, setObjCA] = useState({ PRQC: Array(12).fill(0), LMDS: Array(12).fill(0), LOCATION: Array(12).fill(0) });
  const [savingObj, setSavingObj] = useState(false);

  // Objectifs de production mensuels
  const [objProd, setObjProd] = useState({ PRQC: Array(12).fill(0), LMDS: Array(12).fill(0), LOCATION: Array(12).fill(0) });
  const [savingObjProd, setSavingObjProd] = useState(false);

  // Objectifs de revenus (encaissés) mensuels
  const [objRevenu, setObjRevenu] = useState({ PRQC: Array(12).fill(0), LMDS: Array(12).fill(0), LOCATION: Array(12).fill(0) });
  const [savingObjRevenu, setSavingObjRevenu] = useState(false);

  // Objectifs de marge brute par catégorie de coût
  const [objectifsMarge, setObjectifsMarge] = useState({
    objectif_employes_pct: 30,
    objectif_peinture_pct: 10,
    objectif_materiaux_pct: 5,
    objectif_marge_brute_min: 40,
    objectif_marge_brute_ideal: 55,
  });
  const [savingMarge, setSavingMarge] = useState(false);

  // Objectifs pluie par mois
  const [pluiePcts, setPluiePcts] = useState({});
  const [savingPluie, setSavingPluie] = useState(false);


  // Objectifs par représentant
  const [objReps, setObjReps] = useState([]);
  const [representatives, setRepresentatives] = useState([]);
  const [savingObjRep, setSavingObjRep] = useState(false);
  const [newRepId, setNewRepId] = useState('');

  // Charger depuis MongoDB au montage
  useEffect(() => {
    axios.get(`${API_URL}/api/configuration/targets`).then(r => r.data)
      .then(d => {
        if (d.targets && Object.keys(d.targets).length > 0) {
          // Deep merge: les données MongoDB écrasent les valeurs par défaut,
          // mais les catégories manquantes sont comblées par TARGETS_CONFIG
          const merged = JSON.parse(JSON.stringify(TARGETS_CONFIG)); // deep clone
          for (const [cat, metrics] of Object.entries(d.targets)) {
            if (merged[cat] && typeof metrics === 'object') {
              for (const [metricKey, metricData] of Object.entries(metrics)) {
                if (merged[cat][metricKey] && metricData?.thresholds) {
                  merged[cat][metricKey] = { ...merged[cat][metricKey], ...metricData };
                }
              }
            }
          }
          setTargets(merged);
          const old = localStorage.getItem('financehub_targets');
          if (old) localStorage.removeItem('financehub_targets');
        } else {
          const old = localStorage.getItem('financehub_targets');
          if (old) {
            const parsed = JSON.parse(old);
            setTargets(parsed);
            axios.put(`${API_URL}/api/configuration/targets`, parsed);
            localStorage.removeItem('financehub_targets');
          }
        }
      })
      .catch(() => {});
  }, []);

  // Charger cibles pluie
  useEffect(() => {
    axios.get(`${API_URL}/api/configuration/pluie-cibles`)
      .then(r => r.status === 200 ? r.data : { pcts: {} })
      .then(d => setPluiePcts(d.pcts || {}))
      .catch(() => {});
  }, []);

  const savePluieCibles = async () => {
    setSavingPluie(true);
    try {
      const res = await axios.put(`${API_URL}/api/configuration/pluie-cibles`, { pcts: pluiePcts });
      if (res.status === 200) toast.success('Cibles pluie sauvegardees !');
      else toast.error('Erreur');
    } catch { toast.error('Erreur reseau'); }
    finally { setSavingPluie(false); }
  };


  // Charger objectifs CA mensuels
  useEffect(() => {
    axios.get(`${API_URL}/api/objectifs/${objYear}`).then(r => r.data)
      .then(data => {
        const newObj = { PRQC: Array(12).fill(0), LMDS: Array(12).fill(0), LOCATION: Array(12).fill(0) };
        const newProd = { PRQC: Array(12).fill(0), LMDS: Array(12).fill(0), LOCATION: Array(12).fill(0) };
        const newRevenu = { PRQC: Array(12).fill(0), LMDS: Array(12).fill(0), LOCATION: Array(12).fill(0) };
        data.forEach(o => {
          if (o.type_objectif === "CA_mensuel" && o.mois >= 1 && o.mois <= 12) {
            if (newObj[o.departement]) newObj[o.departement][o.mois - 1] = o.valeur;
          }
          if (o.type_objectif === "production_mensuel" && o.mois >= 1 && o.mois <= 12) {
            if (newProd[o.departement]) newProd[o.departement][o.mois - 1] = o.valeur;
          }
          if (o.type_objectif === "revenu_mensuel" && o.mois >= 1 && o.mois <= 12) {
            if (newRevenu[o.departement]) newRevenu[o.departement][o.mois - 1] = o.valeur;
          }
        });
        setObjCA(newObj);
        setObjProd(newProd);
        setObjRevenu(newRevenu);
      })
      .catch(() => {});
  }, [objYear]);

  // Charger objectifs de marge
  useEffect(() => {
    axios.get(`${API_URL}/api/configuration/objectifs-marge`).then(r => r.data)
      .then(d => setObjectifsMarge(prev => ({ ...prev, ...d })))
      .catch(() => {});
  }, []);

  // Charger représentants et leurs objectifs
  useEffect(() => {
    axios.get(`${API_URL}/api/employees`).then(r => r.data)
      .then(d => {
        const reps = (d.employees || []).filter(e => e.type_employe === 'representant' || e.est_proprietaire);
        setRepresentatives(reps);
      }).catch(() => {});
    
    axios.get(`${API_URL}/api/objectifs-representants/${objYear}`)
      .then(r => r.status === 200 ? r.data : [])
      .then(data => setObjReps(Array.isArray(data) ? data : []))
      .catch(() => setObjReps([]));
  }, [objYear]);

  const addRepObjectif = () => {
    if (!newRepId) return;
    const rep = representatives.find(r => r.id === newRepId);
    if (!rep) return;
    // Ajouter une ligne par département pour ce représentant
    const existing = objReps.filter(o => o.employee_id === newRepId);
    const existingDepts = new Set(existing.map(o => o.departement));
    const newEntries = [];
    for (const d of ['PRQC', 'LMDS', 'LOCATION']) {
      if (!existingDepts.has(d)) {
        newEntries.push({
          employee_id: newRepId,
          employee_nom: `${rep.prenom} ${rep.nom}`,
          departement: d,
          annee: objYear,
          objectifs: Array(12).fill(0),
        });
      }
    }
    if (newEntries.length > 0) {
      setObjReps(prev => [...prev, ...newEntries]);
    }
    setNewRepId('');
  };

  const handleRepObjChange = (repIdx, moisIdx, val) => {
    setObjReps(prev => {
      const updated = [...prev];
      updated[repIdx] = { ...updated[repIdx], objectifs: [...updated[repIdx].objectifs] };
      updated[repIdx].objectifs[moisIdx] = parseFloat(val) || 0;
      return updated;
    });
  };

  const removeRepDept = (repIdx) => {
    setObjReps(prev => prev.filter((_, i) => i !== repIdx));
  };

  const saveObjReps = async () => {
    setSavingObjRep(true);
    try {
      const r = await axios.put(`${API_URL}/api/objectifs-representants/${objYear}`, { representants: objReps });
      if (r.status === 200) toast.success(`Objectifs représentants ${objYear} sauvegardés !`);
      else toast.error('Erreur sauvegarde');
    } catch { toast.error('Erreur réseau'); }
    finally { setSavingObjRep(false); }
  };


  const handleObjChange = (dept, moisIdx, val) => {
    setObjCA(prev => {
      const updated = [...prev[dept]];
      updated[moisIdx] = parseFloat(val) || 0;
      return { ...prev, [dept]: updated };
    });
  };

  const handleObjProdChange = (dept, moisIdx, val) => {
    setObjProd(prev => {
      const updated = [...prev[dept]];
      updated[moisIdx] = parseFloat(val) || 0;
      return { ...prev, [dept]: updated };
    });
  };

  const handleObjRevenuChange = (dept, moisIdx, val) => {
    setObjRevenu(prev => {
      const updated = [...prev[dept]];
      updated[moisIdx] = parseFloat(val) || 0;
      return { ...prev, [dept]: updated };
    });
  };

  const saveObjectifsRevenu = async () => {
    setSavingObjRevenu(true);
    try {
      const r = await axios.put(`${API_URL}/api/objectifs-revenu/mensuel/${objYear}`, objRevenu);
      if (r.status === 200) toast.success(`Objectifs revenus ${objYear} sauvegardés !`);
      else toast.error("Erreur de sauvegarde");
    } catch { toast.error("Erreur réseau"); }
    finally { setSavingObjRevenu(false); }
  };

  const saveObjectifsProd = async () => {
    setSavingObjProd(true);
    try {
      const r = await axios.put(`${API_URL}/api/objectifs-production/mensuel/${objYear}`, objProd);
      if (r.status === 200) toast.success(`Objectifs de production ${objYear} sauvegardés !`);
      else toast.error("Erreur de sauvegarde");
    } catch { toast.error("Erreur réseau"); }
    finally { setSavingObjProd(false); }
  };

  const saveObjectifsCA = async () => {
    setSavingObj(true);
    try {
      const r = await axios.put(`${API_URL}/api/objectifs/mensuel/${objYear}`, objCA);
      if (r.status === 200) toast.success(`Objectifs ventes ${objYear} sauvegardés !`);
      else toast.error("Erreur de sauvegarde");
    } catch { toast.error("Erreur réseau"); }
    finally { setSavingObj(false); }
  };

  const saveObjectifsMarge = async () => {
    setSavingMarge(true);
    try {
      const r = await axios.put(`${API_URL}/api/configuration/objectifs-marge`, objectifsMarge);
      if (r.status === 200) toast.success('Objectifs de marge sauvegardés !');
      else toast.error('Erreur de sauvegarde');
    } catch { toast.error('Erreur réseau'); }
    finally { setSavingMarge(false); }
  };

  const handleThresholdChange = (category, metricKey, thresholdType, value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;
    
    setTargets(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [metricKey]: {
          ...prev[category][metricKey],
          thresholds: {
            ...prev[category][metricKey].thresholds,
            [thresholdType]: numValue
          }
        }
      }
    }));
    setHasChanges(true);
  };

  const saveTargets = async () => {
    setSaving(true);
    try {
      // Toujours envoyer la fusion complète TARGETS_CONFIG + état courant
      // Garantit qu'aucune catégorie n'est jamais perdue
      const payload = deep_merge_js(TARGETS_CONFIG, targets);
      const r = await axios.put(`${API_URL}/api/configuration/targets`, payload);
      if (r.status === 200) {
        setHasChanges(false);
        toast.success("Cibles sauvegardées avec succès !");
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch { toast.error("Erreur réseau"); }
    finally { setSaving(false); }
  };

  const resetToDefaults = async () => {
    setTargets(TARGETS_CONFIG);
    await axios.put(`${API_URL}/api/configuration/targets`, TARGETS_CONFIG);
    setHasChanges(false);
    toast.info("Cibles réinitialisées aux valeurs par défaut");
  };

  if (!targets) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div>
      </div>
    );
  }

  const renderMetricEditor = (category, metricKey, metric) => {
    if (!metric || !metric.thresholds) return null;
    
    const directionLabel = metric.direction === "higher_is_better" 
      ? "Plus élevé = mieux" 
      : "Plus bas = mieux";

    return (
      <div key={metricKey} className="p-4 bg-slate-50 rounded-lg space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-slate-900">{metric.label || metricKey}</h4>
            <p className="text-xs text-slate-500 mt-0.5">
              {directionLabel} • Unité: {metric.unit || "-"}
            </p>
          </div>
          {metric.non_negotiable && (
            <Badge variant="destructive" className="text-xs">Non négociable</Badge>
          )}
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs text-emerald-600 font-medium flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              Vert (Excellent)
            </Label>
            <Input
              type="number"
              step="0.1"
              value={metric.thresholds.green}
              onChange={(e) => handleThresholdChange(category, metricKey, 'green', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs text-amber-600 font-medium flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              Orange (Attention)
            </Label>
            <Input
              type="number"
              step="0.1"
              value={metric.thresholds.orange}
              onChange={(e) => handleThresholdChange(category, metricKey, 'orange', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs text-red-500 font-medium flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-500"></span>
              Rouge (Critique)
            </Label>
            <Input
              type="number"
              step="0.1"
              value={metric.thresholds.red}
              onChange={(e) => handleThresholdChange(category, metricKey, 'red', e.target.value)}
              className="mt-1"
              disabled={metric.direction === "higher_is_better" && metric.thresholds.red === 0}
            />
          </div>
        </div>
        
        {metric.description && (
          <details className="text-xs">
            <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
              Voir l&apos;explication complète
            </summary>
            <p className="mt-2 text-slate-600 whitespace-pre-wrap bg-white p-3 rounded border border-slate-200">
              {metric.description}
            </p>
          </details>
        )}
      </div>
    );
  };

  return (
    <div data-testid="configuration-cibles-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Settings className="w-8 h-8 text-emerald-600" />
            Configuration des Cibles
          </h1>
          <p className="text-slate-500 mt-1">
            Personnalisez les seuils de performance pour vos indicateurs financiers
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={resetToDefaults}
            className="flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Réinitialiser
          </Button>
          <Button 
            onClick={saveTargets}
            disabled={!hasChanges || saving}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </div>
      </div>

      {hasChanges && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3 animate-fade-in">
          <Target className="w-5 h-5 text-amber-600" />
          <p className="text-sm text-amber-800">
            Vous avez des modifications non sauvegardées. N&apos;oubliez pas de sauvegarder!
          </p>
        </div>
      )}

      {/* Légende */}
      <Card className="animate-fade-in delay-100">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Comment ça fonctionne?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
              <span className="w-4 h-4 rounded-full bg-emerald-500 flex-shrink-0"></span>
              <div>
                <p className="font-medium text-emerald-900">Vert</p>
                <p className="text-emerald-700 text-xs">Performance excellente, objectif atteint</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
              <span className="w-4 h-4 rounded-full bg-amber-500 flex-shrink-0"></span>
              <div>
                <p className="font-medium text-amber-900">Orange</p>
                <p className="text-amber-700 text-xs">Zone d&apos;attention, amélioration possible</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
              <span className="w-4 h-4 rounded-full bg-red-500 flex-shrink-0"></span>
              <div>
                <p className="font-medium text-red-900">Rouge</p>
                <p className="text-red-700 text-xs">Zone critique, action requise</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Catégories de cibles */}
      <Tabs defaultValue="objectifs_ca" className="animate-fade-in delay-200">
        <TabsList className="mb-4 flex flex-wrap gap-2 h-auto p-2 bg-slate-100">
          <TabsTrigger value="objectifs_ca" className="flex items-center gap-2 data-[state=active]:bg-white">
            <CalendarDays className="w-4 h-4" />
            Objectifs Ventes Mensuels
          </TabsTrigger>
          <TabsTrigger value="objectifs_production" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Target className="w-4 h-4" />
            Objectifs Production
          </TabsTrigger>
          <TabsTrigger value="objectifs_revenu" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Wallet className="w-4 h-4" />
            Objectifs Revenus
          </TabsTrigger>
          <TabsTrigger value="objectifs_marge" className="flex items-center gap-2 data-[state=active]:bg-white">
            <TrendingUp className="w-4 h-4" />
            Objectifs Marge Brute
          </TabsTrigger>
          {Object.keys(CATEGORY_LABELS).map(cat => {
            const Icon = CATEGORY_ICONS[cat];
            return (
              <TabsTrigger
                key={cat}
                value={cat}
                className="flex items-center gap-2 data-[state=active]:bg-white"
              >
                <Icon className="w-4 h-4" />
                {CATEGORY_LABELS[cat]}
              </TabsTrigger>
            );
          })}
          <TabsTrigger value="pluie" className="flex items-center gap-2 data-[state=active]:bg-white" data-testid="tab-pluie">
            <CloudRain className="w-4 h-4" />
            Jours de pluie
          </TabsTrigger>
        </TabsList>

        {/* Onglet Objectifs Ventes Mensuels */}
        <TabsContent value="objectifs_ca">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CalendarDays className="w-5 h-5 text-emerald-600" />
                    Objectifs de ventes mensuel par département
                  </CardTitle>
                  <CardDescription>Ces objectifs sont utilisés pour la projection de valorisation et la page Ventes.</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Année :</Label>
                  <Input type="number" value={objYear} onChange={e => setObjYear(parseInt(e.target.value))} className="w-24" />
                  <Button onClick={saveObjectifsCA} disabled={savingObj} className="bg-emerald-600 hover:bg-emerald-700">
                    <Save className="w-4 h-4 mr-1" />
                    {savingObj ? "Sauvegarde..." : "Sauvegarder"}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4 font-semibold text-slate-700 min-w-[140px]">Département</th>
                      {MOIS_COURT.map(m => (
                        <th key={m} className="text-center py-2 px-1 font-semibold text-slate-600 min-w-[80px]">{m}</th>
                      ))}
                      <th className="text-center py-2 px-1 font-semibold text-emerald-700 min-w-[100px]">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DEPTS.map(({ key, label, color }) => {
                      const total = objCA[key].reduce((s, v) => s + (v || 0), 0);
                      return (
                        <tr key={key} className="border-b last:border-0">
                          <td className="py-2 pr-4 font-medium text-slate-800">{label}</td>
                          {objCA[key].map((val, i) => (
                            <td key={val.id || val._id || i} className="py-1 px-1">
                              <Input
                                type="number"
                                min="0"
                                value={val || ''}
                                placeholder="0"
                                onChange={e => handleObjChange(key, i, e.target.value)}
                                className={`w-full text-center text-xs h-8 border-${color}-200 focus:border-${color}-400`}
                              />
                            </td>
                          ))}
                          <td className={`py-2 px-1 text-center font-bold text-${color}-700`}>
                            {formatCurrency(total)}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="bg-slate-50">
                      <td className="py-2 pr-4 font-semibold text-slate-700">Total mensuel</td>
                      {Array.from({ length: 12 }, (_, i) => {
                        const totalMois = DEPTS.reduce((s, { key }) => s + (objCA[key][i] || 0), 0);
                        return (
                          <td key={i} className="py-2 px-1 text-center font-semibold text-slate-700 text-xs">
                            {totalMois > 0 ? formatCurrency(totalMois) : '—'}
                          </td>
                        );
                      })}
                      <td className="py-2 px-1 text-center font-bold text-emerald-700">
                        {formatCurrency(DEPTS.reduce((s, { key }) => s + objCA[key].reduce((a, v) => a + (v || 0), 0), 0))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-400 mt-3">
                Entrez les objectifs de ventes (en dollars, sans taxes) pour chaque département et chaque mois.
              </p>
            </CardContent>
          </Card>

          {/* Objectifs par représentant */}
          <Card className="mt-4">
            <CardHeader>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Objectifs par représentant — {objYear}
                  </CardTitle>
                  <CardDescription>Objectifs individuels de signature par représentant. Utilisés dans les SMS de notification.</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={newRepId}
                    onChange={e => setNewRepId(e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm"
                    data-testid="select-rep"
                  >
                    <option value="">Ajouter un représentant...</option>
                    {representatives.filter(r => !objReps.find(o => o.employee_id === r.id)).map(r => (
                      <option key={r.id} value={r.id}>{r.prenom} {r.nom}</option>
                    ))}
                  </select>
                  <Button onClick={addRepObjectif} disabled={!newRepId} variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-1" /> Ajouter
                  </Button>
                  <Button onClick={saveObjReps} disabled={savingObjRep} className="bg-blue-600 hover:bg-blue-700">
                    <Save className="w-4 h-4 mr-1" />
                    {savingObjRep ? "Sauvegarde..." : "Sauvegarder"}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {objReps.length === 0 ? (
                <p className="text-sm text-slate-400 italic py-4 text-center">Aucun objectif par représentant configuré. Ajoutez un représentant ci-dessus.</p>
              ) : (() => {
                // Grouper par représentant
                const repIds = [...new Set(objReps.map(o => o.employee_id))];
                const DEPT_COLORS = { PRQC: 'emerald', LMDS: 'blue', LOCATION: 'amber' };
                const DEPT_LABELS = { PRQC: 'Peinture (PRQC)', LMDS: 'LMDS', LOCATION: 'Location' };
                return (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-2 font-semibold text-slate-700 min-w-[180px]">Représentant / Dept</th>
                        {MOIS_COURT.map(m => (
                          <th key={m} className="text-center py-2 px-1 font-semibold text-slate-600 min-w-[65px]">{m}</th>
                        ))}
                        <th className="text-center py-2 px-1 font-semibold text-blue-700 min-w-[85px]">Total</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {repIds.map(repId => {
                        const repRows = objReps.filter(o => o.employee_id === repId);
                        const repNom = repRows[0]?.employee_nom || '?';
                        const repTotal = repRows.reduce((s, r) => s + (r.objectifs || []).reduce((a, v) => a + (v || 0), 0), 0);
                        return (
                          <React.Fragment key={repId}>
                            <tr className="bg-slate-50 border-t-2">
                              <td colSpan={14} className="py-2 px-2 font-bold text-slate-800">{repNom} <span className="text-xs font-normal text-slate-500 ml-2">Total: {formatCurrency(repTotal)}</span></td>
                            </tr>
                            {repRows.map((row) => {
                              const rIdx = objReps.indexOf(row);
                              const total = (row.objectifs || []).reduce((s, v) => s + (v || 0), 0);
                              const color = DEPT_COLORS[row.departement] || 'slate';
                              return (
                                <tr key={`${repId}-${row.departement}`} className="border-b last:border-0">
                                  <td className={`py-1.5 pr-2 pl-4 font-medium text-${color}-700 text-xs`}>{DEPT_LABELS[row.departement] || row.departement}</td>
                                  {(row.objectifs || Array(12).fill(0)).map((val, i) => (
                                    <td key={val.id || val._id || i} className="py-0.5 px-0.5">
                                      <Input
                                        type="number" min="0" value={val || ''} placeholder="0"
                                        onChange={e => handleRepObjChange(rIdx, i, e.target.value)}
                                        className={`w-full text-center text-xs h-7 border-${color}-200`}
                                      />
                                    </td>
                                  ))}
                                  <td className={`py-1.5 px-1 text-center font-bold text-${color}-700 text-xs`}>
                                    {formatCurrency(total)}
                                  </td>
                                  <td className="py-1.5">
                                    <button onClick={() => removeRepDept(rIdx)} className="text-red-400 hover:text-red-600 text-xs" title="Retirer">x</button>
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet Objectifs Production Mensuels */}
        <TabsContent value="objectifs_production">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-600" />
                    Objectifs de production mensuel par département
                  </CardTitle>
                  <CardDescription>Production réalisée cible (PRQC: prorata jours, LMDS: installations, Location: factures finales). Partagez la même année que les objectifs CA.</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Année :</Label>
                  <Input type="number" value={objYear} onChange={e => setObjYear(parseInt(e.target.value))} className="w-24" />
                  <Button onClick={saveObjectifsProd} disabled={savingObjProd} className="bg-blue-600 hover:bg-blue-700">
                    <Save className="w-4 h-4 mr-1" />
                    {savingObjProd ? "Sauvegarde..." : "Sauvegarder"}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4 font-semibold text-slate-700 min-w-[140px]">Département</th>
                      {MOIS_COURT.map(m => (
                        <th key={m} className="text-center py-2 px-1 font-semibold text-slate-600 min-w-[80px]">{m}</th>
                      ))}
                      <th className="text-center py-2 px-1 font-semibold text-blue-700 min-w-[100px]">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DEPTS.map(({ key, label, color }) => {
                      const total = objProd[key].reduce((s, v) => s + (v || 0), 0);
                      return (
                        <tr key={key} className="border-b last:border-0">
                          <td className="py-2 pr-4 font-medium text-slate-800">{label}</td>
                          {objProd[key].map((val, i) => (
                            <td key={val.id || val._id || i} className="py-1 px-1">
                              <Input
                                type="number"
                                min="0"
                                value={val || ''}
                                placeholder="0"
                                onChange={e => handleObjProdChange(key, i, e.target.value)}
                                className={`w-full text-center text-xs h-8 border-${color}-200 focus:border-${color}-400`}
                              />
                            </td>
                          ))}
                          <td className={`py-2 px-1 text-center font-bold text-${color}-700`}>
                            {formatCurrency(total)}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="bg-slate-50">
                      <td className="py-2 pr-4 font-semibold text-slate-700">Total mensuel</td>
                      {Array.from({ length: 12 }, (_, i) => {
                        const totalMois = DEPTS.reduce((s, { key }) => s + (objProd[key][i] || 0), 0);
                        return (
                          <td key={i} className="py-2 px-1 text-center font-semibold text-slate-700 text-xs">
                            {totalMois > 0 ? formatCurrency(totalMois) : '—'}
                          </td>
                        );
                      })}
                      <td className="py-2 px-1 text-center font-bold text-blue-700">
                        {formatCurrency(DEPTS.reduce((s, { key }) => s + objProd[key].reduce((a, v) => a + (v || 0), 0), 0))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-400 mt-3">
                Entrez les objectifs de production (en dollars, sans taxes) pour chaque département et chaque mois.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet Objectifs Revenus Mensuels */}
        <TabsContent value="objectifs_revenu">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-purple-600" />
                    Objectifs de revenus mensuel par département
                  </CardTitle>
                  <CardDescription>Revenus encaissés cible (argent reçu via QuickBooks). Comparé aux revenus réels dans la Vue d&apos;ensemble.</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Année :</Label>
                  <Input type="number" value={objYear} onChange={e => setObjYear(parseInt(e.target.value))} className="w-24" />
                  <Button onClick={saveObjectifsRevenu} disabled={savingObjRevenu} className="bg-purple-600 hover:bg-purple-700" data-testid="save-objectifs-revenu">
                    <Save className="w-4 h-4 mr-1" />
                    {savingObjRevenu ? "Sauvegarde..." : "Sauvegarder"}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4 font-semibold text-slate-700 min-w-[140px]">Département</th>
                      {MOIS_COURT.map(m => (
                        <th key={m} className="text-center py-2 px-1 font-semibold text-slate-600 min-w-[80px]">{m}</th>
                      ))}
                      <th className="text-center py-2 px-1 font-semibold text-purple-700 min-w-[100px]">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DEPTS.map(({ key, label, color }) => {
                      const total = objRevenu[key].reduce((s, v) => s + (v || 0), 0);
                      return (
                        <tr key={key} className="border-b last:border-0">
                          <td className="py-2 pr-4 font-medium text-slate-800">{label}</td>
                          {objRevenu[key].map((val, i) => (
                            <td key={val.id || val._id || i} className="py-1 px-1">
                              <Input
                                type="number"
                                min="0"
                                value={val || ''}
                                placeholder="0"
                                onChange={e => handleObjRevenuChange(key, i, e.target.value)}
                                className={`w-full text-center text-xs h-8 border-${color}-200 focus:border-${color}-400`}
                                data-testid={`obj-revenu-${key}-${i}`}
                              />
                            </td>
                          ))}
                          <td className={`py-2 px-1 text-center font-bold text-${color}-700`}>
                            {formatCurrency(total)}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="bg-slate-50">
                      <td className="py-2 pr-4 font-semibold text-slate-700">Total mensuel</td>
                      {Array.from({ length: 12 }, (_, i) => {
                        const totalMois = DEPTS.reduce((s, { key }) => s + (objRevenu[key][i] || 0), 0);
                        return (
                          <td key={i} className="py-2 px-1 text-center font-semibold text-slate-700 text-xs">
                            {totalMois > 0 ? formatCurrency(totalMois) : '—'}
                          </td>
                        );
                      })}
                      <td className="py-2 px-1 text-center font-bold text-purple-700">
                        {formatCurrency(DEPTS.reduce((s, { key }) => s + objRevenu[key].reduce((a, v) => a + (v || 0), 0), 0))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-400 mt-3">
                Entrez les objectifs de revenus encaissés (en dollars, sans taxes) pour chaque département et chaque mois.
                Ces objectifs sont comparés aux revenus réels (QuickBooks) dans la Vue d&apos;ensemble.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {Object.entries(CATEGORY_LABELS).map(([category, label]) => {
          const metrics = targets[category];
          if (!metrics) return null;
          
          const Icon = CATEGORY_ICONS[category];
          
          return (
            <TabsContent key={category} value={category}>
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${CATEGORY_COLORS[category]}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{label}</CardTitle>
                      <CardDescription>
                        {Object.keys(metrics).filter(k => metrics[k]?.thresholds).length} indicateurs configurables
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="space-y-2">
                    {Object.entries(metrics).map(([metricKey, metric]) => {
                      if (!metric?.thresholds) return null;
                      
                      return (
                        <AccordionItem 
                          key={metricKey} 
                          value={metricKey}
                          className="border rounded-lg px-4"
                        >
                          <AccordionTrigger className="hover:no-underline">
                            <div className="flex items-center gap-3 text-left">
                              <span className="font-medium">{metric.label || metricKey}</span>
                              <Badge variant="outline" className="text-xs">
                                {metric.unit || "-"}
                              </Badge>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            {renderMetricEditor(category, metricKey, metric)}
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
        
        {/* Onglet Objectifs Marge Brute */}
        <TabsContent value="objectifs_marge">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-600" />Objectifs de Marge Brute par Projet</CardTitle>
                  <CardDescription>Définissez les cibles de coûts pour chaque catégorie de dépenses par rapport au revenu du projet.</CardDescription>
                </div>
                <Button onClick={saveObjectifsMarge} disabled={savingMarge} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-objectifs-marge-btn">
                  <Save className="w-4 h-4 mr-2" />{savingMarge ? 'Sauvegarde...' : 'Sauvegarder'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { key: 'objectif_employes_pct', label: 'Coût des employés (% du revenu)', desc: 'Part maximale des salaires sur le revenu brut du projet', color: 'blue', icon: '👥' },
                  { key: 'objectif_peinture_pct', label: 'Coût de la peinture (% du revenu)', desc: 'Part maximale des produits (peinture) sur le revenu brut', color: 'amber', icon: '🎨' },
                  { key: 'objectif_materiaux_pct', label: 'Coût des matériaux (% du revenu)', desc: 'Part maximale des matériaux et frais divers sur le revenu', color: 'purple', icon: '🔧' },
                  { key: 'objectif_marge_brute_min', label: 'Marge brute minimum (%)', desc: 'Seuil d\'alerte: en dessous de ce % la marge est insuffisante', color: 'red', icon: '⚠️' },
                  { key: 'objectif_marge_brute_ideal', label: 'Marge brute idéale (%)', desc: 'Cible de marge brute souhaitée pour chaque projet', color: 'green', icon: '✅' },
                ].map(({ key, label, desc, color, icon }) => (
                  <div key={key} className={`p-4 rounded-lg border border-${color}-100 bg-${color}-50`}>
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{icon}</span>
                      <div className="flex-1">
                        <Label className="font-semibold text-gray-800">{label}</Label>
                        <p className="text-xs text-gray-500 mt-0.5 mb-2">{desc}</p>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.5"
                            value={objectifsMarge[key]}
                            onChange={(e) => setObjectifsMarge(prev => ({ ...prev, [key]: parseFloat(e.target.value) || 0 }))}
                            className="w-24 font-semibold"
                            data-testid={`objectif-${key}`}
                          />
                          <span className="text-gray-500 font-medium">%</span>
                          <div className="flex-1 bg-gray-200 rounded-full h-2 ml-2">
                            <div
                              className={`h-2 rounded-full bg-${color}-500`}
                              style={{ width: `${Math.min(objectifsMarge[key], 100)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
                <p className="font-semibold mb-1">💡 Comment ça fonctionne ?</p>
                <p>Ces objectifs sont utilisés dans l'onglet "Statistiques" de chaque projet pour afficher des indicateurs visuels (rouge/orange/vert) sur la performance de votre marge brute.</p>
                <p className="mt-1">Exemple: Si vos coûts employés dépassent {objectifsMarge.objectif_employes_pct}% du revenu, une alerte apparaîtra dans le projet.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ Onglet Jours de Pluie ═══ */}
        <TabsContent value="pluie" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CloudRain className="w-5 h-5 text-cyan-600" />
                Cibles heures jours de pluie par mois
              </CardTitle>
              <CardDescription>
                Definissez le % des heures travaillables a securiser en projets "jours de pluie" pour chaque mois (mai a octobre).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {[4, 5, 6, 7, 8, 9].map(m => (
                  <div key={m} className="border rounded-lg p-3 text-center">
                    <Label className="text-xs font-semibold text-gray-600">{MOIS_COURT[m]}</Label>
                    <div className="flex items-center gap-1 mt-1">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        className="h-8 text-center text-sm"
                        value={pluiePcts[m] ?? ''}
                        onChange={e => setPluiePcts(p => ({ ...p, [m]: e.target.value === '' ? '' : Number(e.target.value) }))}
                        data-testid={`pluie-pct-${m}`}
                      />
                      <span className="text-xs text-gray-500">%</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end mt-4">
                <Button onClick={savePluieCibles} disabled={savingPluie} data-testid="save-pluie-cibles">
                  <Save className="w-4 h-4 mr-1" />
                  {savingPluie ? 'Sauvegarde...' : 'Sauvegarder'}
                </Button>
              </div>
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 text-sm text-cyan-700 mt-3">
                <p className="font-semibold mb-1">Comment ca fonctionne ?</p>
                <p>Ce % represente la portion des heures travaillables du mois que chaque equipe devrait avoir en projets "jours de pluie". Exemple : 20% en mai = si une equipe a 800h de capacite, elle devrait avoir 160h de projets pluie planifies.</p>
                <p className="mt-1">Les indicateurs (vert/jaune/rouge) apparaissent dans le popup "Projets temps de pluie" du calendrier de production.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

    </div>
  );
};

export default ConfigurationCibles;
